import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getCalls } from '../services/api';
import socket from '../services/socket';
import { SafetyBadge, CallStatusBadge } from '../components/StatusBadge';

const ACTION_LABELS = {
  visit_branch: 'Visit Branch',
  send_link: 'Send KYC Link',
  callback_agent: 'Callback / Agent',
};

function CallLogs() {
  const [calls, setCalls] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [filter, setFilter] = useState({ safety_status: '', call_status: '' });
  const navigate = useNavigate();

  const loadCalls = async () => {
    try {
      const params = { page, limit: 20 };
      if (filter.safety_status) params.safety_status = filter.safety_status;
      if (filter.call_status) params.call_status = filter.call_status;
      const res = await getCalls(params);
      setCalls(res.data.calls);
      setTotal(res.data.total);
      setPages(res.data.pages);
    } catch (err) {
      console.error('Failed to load calls:', err);
    }
  };

  useEffect(() => { loadCalls(); }, [page, filter]);

  useEffect(() => {
    socket.on('call_update', loadCalls);
    socket.on('call_completed', loadCalls);
    return () => {
      socket.off('call_update', loadCalls);
      socket.off('call_completed', loadCalls);
    };
  }, [page, filter]);

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h4 className="fw-bold mb-1">Call Logs</h4>
          <p className="text-muted mb-0">{total} total calls</p>
        </div>
        <div className="d-flex gap-2">
          <select className="form-select form-select-sm" style={{ width: 160 }}
            value={filter.safety_status}
            onChange={(e) => { setFilter({ ...filter, safety_status: e.target.value }); setPage(1); }}>
            <option value="">All KYC Status</option>
            <option value="verified">Verified</option>
            <option value="action_selected">Action Selected</option>
            <option value="no_response">No Response</option>
            <option value="unknown">Unknown</option>
          </select>
          <select className="form-select form-select-sm" style={{ width: 160 }}
            value={filter.call_status}
            onChange={(e) => { setFilter({ ...filter, call_status: e.target.value }); setPage(1); }}>
            <option value="">All Call Status</option>
            <option value="completed">Completed</option>
            <option value="failed">Failed</option>
            <option value="dialing">Dialing</option>
            <option value="answered">Answered</option>
            <option value="no_answer">No Answer</option>
            <option value="unable_to_connect">Unable To Connect</option>
            <option value="disconnected">Disconnected</option>
          </select>
        </div>
      </div>

      <div className="card border-0 shadow-sm" style={{ borderRadius: 16 }}>
        <div className="table-responsive">
          <table className="table table-hover align-middle mb-0">
            <thead className="table-light">
              <tr>
                <th className="ps-4">Employee</th>
                <th>Phone</th>
                <th>Call Status</th>
                <th>KYC Status</th>
                <th>Selected Action</th>
                <th>Time</th>
              </tr>
            </thead>
            <tbody>
              {calls.length === 0 ? (
                <tr>
                  <td colSpan="6" className="text-center py-5 text-muted">
                    <i className="bi bi-inbox fs-1 d-block mb-2"></i>
                    No calls found
                  </td>
                </tr>
              ) : (
                calls.map((call) => (
                  <tr key={call._id} onClick={() => navigate(`/calls/${call._id}`)}>
                    <td className="ps-4">
                      <strong>{call.employee_name}</strong>
                    </td>
                    <td className="text-muted">{call.phone}</td>
                    <td><CallStatusBadge status={call.call_status} /></td>
                    <td><SafetyBadge status={call.safety_status} /></td>
                    <td>
                      {call.selected_action ? (
                        <span className="badge bg-outline-danger border border-danger text-danger">
                          {ACTION_LABELS[call.selected_action] || call.selected_action}
                        </span>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>
                    <td className="text-muted small">
                      {new Date(call.created_at).toLocaleString('en-US', {
                        month: 'short', day: 'numeric',
                        hour: '2-digit', minute: '2-digit',
                      })}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {pages > 1 && (
          <div className="card-footer bg-white border-0 d-flex justify-content-center py-3">
            <nav>
              <ul className="pagination pagination-sm mb-0">
                <li className={`page-item ${page <= 1 ? 'disabled' : ''}`}>
                  <button className="page-link" onClick={() => setPage(page - 1)}>Prev</button>
                </li>
                {Array.from({ length: Math.min(pages, 5) }, (_, i) => i + 1).map((p) => (
                  <li key={p} className={`page-item ${p === page ? 'active' : ''}`}>
                    <button className="page-link" onClick={() => setPage(p)}>{p}</button>
                  </li>
                ))}
                <li className={`page-item ${page >= pages ? 'disabled' : ''}`}>
                  <button className="page-link" onClick={() => setPage(page + 1)}>Next</button>
                </li>
              </ul>
            </nav>
          </div>
        )}
      </div>
    </div>
  );
}

export default CallLogs;
